import React, { useEffect, useRef, useState } from 'react';

import Icon from '../common/Icon';

const PREVIEW_SRC = `${process.env.PUBLIC_URL || ''}/demo/hero-preview.mp4`;
const PREVIEW_POSTER = `${process.env.PUBLIC_URL || ''}/demo/hero-preview-poster.jpg`;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const PREVIEW_DESCRIPTION =
  'KrishvaQ product preview: an interview question is captured as live text and an AI answer appears in the desktop overlay';

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(REDUCED_MOTION_QUERY).matches
  );
}

/** True while the element is at least partly on screen (always true where
 * IntersectionObserver is unavailable, so playback is never stuck off). */
function useInView(ref) {
  const [inView, setInView] = useState(true);

  useEffect(() => {
    if (!ref.current || typeof window.IntersectionObserver === 'undefined') return undefined;
    const observer = new window.IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.25 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);

  return inView;
}

/** False while the browser tab is hidden. */
function usePageVisible() {
  const [visible, setVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState !== 'hidden'
  );

  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  return visible;
}

/**
 * Hero product preview: a short, silent clip that loops on its own while it
 * is on screen. Independent of the full demo in DemoVideoSection.
 *
 * - Autoplays muted and inline; native controls are hidden.
 * - A pause/play button lets visitors stop the motion.
 * - With prefers-reduced-motion, only the poster shows until the visitor
 *   presses play, and the video is not downloaded before then.
 * - Pauses when scrolled out of view or when the tab is hidden.
 * - Falls back to the poster (or a static card) if the clip fails to load.
 */
function HeroVideoPreview() {
  const frameRef = useRef(null);
  const videoRef = useRef(null);
  const [autoplayOnLoad] = useState(() => !prefersReducedMotion());
  const [wantsPlay, setWantsPlay] = useState(autoplayOnLoad);
  // 'video' -> clip available; 'poster' -> clip failed; 'none' -> poster failed too.
  const [media, setMedia] = useState('video');
  const inView = useInView(frameRef);
  const pageVisible = usePageVisible();

  const shouldPlay = media === 'video' && wantsPlay && inView && pageVisible;

  // Turning reduced motion on while the page is open stops the loop.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = (event) => {
      if (event.matches) setWantsPlay(false);
    };
    if (query.addEventListener) {
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    }
    query.addListener(onChange);
    return () => query.removeListener(onChange);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || media !== 'video') return;
    if (!shouldPlay) {
      if (!video.paused) video.pause();
      return;
    }
    video.muted = true;
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch((error) => {
        // Autoplay refused (e.g. power saving): show the play button instead.
        // An AbortError only means a pause() interrupted this play().
        if (error && error.name === 'NotAllowedError') setWantsPlay(false);
      });
    }
  }, [shouldPlay, media]);

  const label = wantsPlay ? 'Pause product preview' : 'Play product preview';

  return (
    <figure className="hero-preview-card">
      <div className="hero-preview-frame" ref={frameRef}>
        {media === 'video' && (
          <video
            ref={videoRef}
            className="hero-preview-media"
            src={PREVIEW_SRC}
            poster={PREVIEW_POSTER}
            width="1280"
            height="720"
            autoPlay={autoplayOnLoad}
            loop
            muted
            playsInline
            disablePictureInPicture
            preload={autoplayOnLoad ? 'auto' : 'none'}
            onError={() => setMedia('poster')}
            aria-label={PREVIEW_DESCRIPTION}
          />
        )}
        {media === 'poster' && (
          <img
            className="hero-preview-media"
            src={PREVIEW_POSTER}
            width="1280"
            height="720"
            alt={PREVIEW_DESCRIPTION}
            onError={() => setMedia('none')}
          />
        )}
        {media === 'none' && (
          <div className="hero-preview-fallback">
            <Icon name="waveform" size={22} />
            <span>Live transcript · Translation · AI answers</span>
          </div>
        )}

        {media === 'video' && (
          <button
            type="button"
            className="hero-preview-toggle"
            onClick={() => setWantsPlay((value) => !value)}
            aria-label={label}
            title={label}
          >
            <Icon name={wantsPlay ? 'pause' : 'play'} size={14} />
          </button>
        )}
      </div>
    </figure>
  );
}

export default HeroVideoPreview;
